// ---------------------------------------------------------------------------
// DSS-14: Full transform builder — translate, rotate, scale, skew, flip, origin
// ---------------------------------------------------------------------------
import { useState, useCallback, useId } from "react";
import { Move } from "lucide-react";
import { Section, SliderRow, UnitRow, FlipButton, ToggleSwitch } from "./transform-primitives";

// ── Types ──
export interface TransformConfig {
  readonly translateX: number;
  readonly translateXUnit: "px" | "%";
  readonly translateY: number;
  readonly translateYUnit: "px" | "%";
  readonly rotate: number;
  readonly is3d: boolean;
  readonly rotateX: number;
  readonly rotateY: number;
  readonly rotateZ: number;
  readonly perspective: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly skewX: number;
  readonly skewY: number;
  readonly flipH: boolean;
  readonly flipV: boolean;
  readonly originX: string;
  readonly originY: string;
  readonly state: "normal" | "hover";
}

// ── Constants ──
const ORIGIN_POINTS = [
  ["left", "top"], ["center", "top"], ["right", "top"],
  ["left", "center"], ["center", "center"], ["right", "center"],
  ["left", "bottom"], ["center", "bottom"], ["right", "bottom"],
] as const;

const ORIGIN_LABELS: Record<string, string> = {
  "left-top": "TL", "center-top": "TC", "right-top": "TR",
  "left-center": "ML", "center-center": "C", "right-center": "MR",
  "left-bottom": "BL", "center-bottom": "BC", "right-bottom": "BR",
};

// ── Utilities ──
function safeNum(raw: number, fallback: number): number {
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(safeNum(val, min), min), max);
}

export function generateTransformCss(c: TransformConfig): string {
  const parts: string[] = [];

  if (c.translateX !== 0) parts.push(`translateX(${c.translateX}${c.translateXUnit})`);
  if (c.translateY !== 0) parts.push(`translateY(${c.translateY}${c.translateYUnit})`);

  if (c.is3d) {
    if (c.rotateX !== 0) parts.push(`rotateX(${c.rotateX}deg)`);
    if (c.rotateY !== 0) parts.push(`rotateY(${c.rotateY}deg)`);
    if (c.rotateZ !== 0) parts.push(`rotateZ(${c.rotateZ}deg)`);
  } else if (c.rotate !== 0) {
    parts.push(`rotate(${c.rotate}deg)`);
  }

  const sx = c.flipH ? -(c.scaleX / 100) : c.scaleX / 100;
  const sy = c.flipV ? -(c.scaleY / 100) : c.scaleY / 100;
  if (sx !== 1 || sy !== 1) parts.push(`scale(${sx}, ${sy})`);

  if (c.skewX !== 0) parts.push(`skewX(${c.skewX}deg)`);
  if (c.skewY !== 0) parts.push(`skewY(${c.skewY}deg)`);

  const transform = parts.length > 0 ? parts.join(" ") : "none";
  const origin = `${c.originX} ${c.originY}`;
  const lines: string[] = [];

  if (c.is3d && c.perspective > 0) lines.push(`perspective: ${c.perspective}px;`);
  lines.push(`transform: ${transform};`);
  if (origin !== "center center") lines.push(`transform-origin: ${origin};`);

  return lines.join("\n");
}

// ── Props ──
interface TransformBuilderProps {
  readonly value: TransformConfig;
  readonly onChange: (config: TransformConfig) => void;
}

// ── Component ──
export default function TransformBuilder({ value, onChange }: TransformBuilderProps) {
  const uid = useId();
  const [activeState, setActiveState] = useState<"normal" | "hover">(value.state);

  const update = useCallback(
    (patch: Partial<TransformConfig>) => { onChange({ ...value, ...patch }); },
    [value, onChange],
  );

  const handleStateTab = useCallback((s: "normal" | "hover") => {
    setActiveState(s);
    update({ state: s });
  }, [update]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Move className="h-3.5 w-3.5" />
        Transform
      </div>

      {/* State tabs */}
      <div className="flex rounded-md border border-[var(--color-border)] p-0.5">
        {(["normal", "hover"] as const).map((s) => (
          <button key={s} type="button" onClick={() => handleStateTab(s)}
            className={[
              "flex-1 rounded-sm px-3 py-1 text-[10px] font-medium capitalize",
              activeState === s
                ? "bg-[var(--color-primary)] text-white"
                : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
            ].join(" ")}>
            {s}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        <Section label="Translate">
          <UnitRow uid={uid} axis="X" value={value.translateX} unit={value.translateXUnit}
            onValue={(v) => update({ translateX: clamp(v, -500, 500) })}
            onUnit={(u) => update({ translateXUnit: u })} />
          <UnitRow uid={uid} axis="Y" value={value.translateY} unit={value.translateYUnit}
            onValue={(v) => update({ translateY: clamp(v, -500, 500) })}
            onUnit={(u) => update({ translateYUnit: u })} />
        </Section>

        <Section label="Rotate">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-[10px] text-[var(--color-muted-foreground)]">3D</span>
            <ToggleSwitch isOn={value.is3d} onToggle={() => update({ is3d: !value.is3d })} />
          </div>
          {value.is3d ? (
            <>
              <SliderRow id={`${uid}-rx`} label="X" min={0} max={360} value={value.rotateX} unit="deg" onChange={(v) => update({ rotateX: v })} />
              <SliderRow id={`${uid}-ry`} label="Y" min={0} max={360} value={value.rotateY} unit="deg" onChange={(v) => update({ rotateY: v })} />
              <SliderRow id={`${uid}-rz`} label="Z" min={0} max={360} value={value.rotateZ} unit="deg" onChange={(v) => update({ rotateZ: v })} />
              <SliderRow id={`${uid}-persp`} label="Persp" min={100} max={2000} value={value.perspective} unit="px" onChange={(v) => update({ perspective: v })} />
            </>
          ) : (
            <SliderRow id={`${uid}-rot`} label="Angle" min={0} max={360} value={value.rotate} unit="deg" onChange={(v) => update({ rotate: v })} />
          )}
        </Section>

        <Section label="Scale">
          <SliderRow id={`${uid}-sx`} label="X" min={0} max={200} value={value.scaleX} unit="%" onChange={(v) => update({ scaleX: v })} />
          <SliderRow id={`${uid}-sy`} label="Y" min={0} max={200} value={value.scaleY} unit="%" onChange={(v) => update({ scaleY: v })} />
        </Section>

        <Section label="Skew">
          <SliderRow id={`${uid}-skx`} label="X" min={-45} max={45} value={value.skewX} unit="deg" onChange={(v) => update({ skewX: v })} />
          <SliderRow id={`${uid}-sky`} label="Y" min={-45} max={45} value={value.skewY} unit="deg" onChange={(v) => update({ skewY: v })} />
        </Section>

        <Section label="Flip">
          <div className="flex gap-2">
            <FlipButton label="Horizontal" isActive={value.flipH} onClick={() => update({ flipH: !value.flipH })} />
            <FlipButton label="Vertical" isActive={value.flipV} onClick={() => update({ flipV: !value.flipV })} />
          </div>
        </Section>

        <Section label="Origin">
          <div className="grid w-fit grid-cols-3 gap-1">
            {ORIGIN_POINTS.map(([ox, oy]) => {
              const key = `${ox}-${oy}`;
              const isActive = value.originX === ox && value.originY === oy;
              return (
                <button key={key} type="button" onClick={() => update({ originX: ox, originY: oy })}
                  className={[
                    "h-6 w-6 rounded text-[8px] font-bold",
                    isActive
                      ? "bg-[var(--color-primary)] text-white"
                      : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-border)]",
                  ].join(" ")}>
                  {ORIGIN_LABELS[key]}
                </button>
              );
            })}
          </div>
        </Section>
      </div>

      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateTransformCss(value)}
        </code>
      </div>
    </div>
  );
}
